import React from "react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { CompressionResult } from "@/utils/imageCompression";

interface ImageCompressionStatusProps {
  isCompressing: boolean;
  compressionResult?: CompressionResult;
  error?: string;
  progress?: number;
}

const ImageCompressionStatus = ({ 
  isCompressing, 
  compressionResult, 
  error,
  progress = 0 
}: ImageCompressionStatusProps) => {
  if (error) {
    return (
      <div className="flex items-center gap-2 p-2 bg-destructive/10 rounded-lg">
        <AlertCircle className="h-4 w-4 text-destructive" />
        <span className="text-sm text-destructive">{error}</span>
      </div>
    );
  }

  if (isCompressing) {
    return (
      <div className="space-y-2 p-2 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Compressing image...</span>
        </div>
        <Progress value={progress} className="w-full h-2" />
      </div>
    );
  }

  if (compressionResult) {
    return (
      <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-950/20 rounded-lg">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <div className="flex items-center gap-2 text-sm">
          <span>Compressed:</span>
          <Badge variant="secondary">
            {(compressionResult.originalSize / 1024 / 1024).toFixed(1)}MB → {(compressionResult.compressedSize / 1024 / 1024).toFixed(1)}MB
          </Badge>
          <Badge variant="outline">
            {compressionResult.compressionRatio}% smaller
          </Badge>
        </div>
      </div>
    );
  }

  return null;
};

export default ImageCompressionStatus;